import clsx from "clsx";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue } from "recoil";

import { SVG_MUI } from "@ryde/assets";
import {
  CheckboxUser,
  DraggableWrapper,
  FramerTooltip,
  HubRideCompanionMark,
  PassengerStations,
  RideBoardingIndicator,
  RidePassengerTooltip,
  TStationInfoWithOrderIndex,
  useDecorateWithMarkLocal,
} from "@ryde/controls";
import {
  canDragPassengerByProfileScope,
  canRemovePassengerByProfileScope,
  CURRENT_STATION_EMOJI,
  DragTypeEnum,
  generateDragIdKanbanPassenger,
  getPassengerMoveScopeRestrictions,
  getPassengerNameColor,
  getPassengerScopeRestrictions,
  getPassengerScopeRestrictionTooltipKeys,
  getPassengerSexColor,
  getRidePassengerStationName,
  getRidePassengerStationTime,
  hasMoveScopeRestrictions,
  hasPassengerScopeRestrictions,
  IPassengerRowKanban,
  isHubRideCompanionPassenger,
  IStationInfo,
  KanbanPassengerDragSlotEnum,
  NO_VALUE_DISPLAY,
  onActivateKeyDown,
  systemSettingsDtoAtom,
  useDrawers,
  useFormats,
  useHasPermissions,
  useProfileAuthorizationCheck,
} from "@ryde/shared";

import { KanbanGridPassengerRowStyled } from "../rides.kanban.item.styled";
import { getKanbanPassengerDragSlot } from "./rides.kanban.item.passengers.row.utils";
import {
  KanbanPassengerDragGrip,
  KanbanPassengerDragGripPlaceholder,
} from "./rides.kanban.passenger-drag-grip";
import { KanbanPassengerScopeRestrictionMarker } from "./rides.kanban.passenger-scope-restriction-marker";

export const KanbanPassengersRow: FC<IPassengerRowKanban> = props => {
  const {
    rideDto,
    passenger,
    deletePassengerHandler,
    chosen,
    setChosenToggle,
    isDragClone,
    dragSelectedIds,
    stationIdsOrderIndexMap,
  } = props;
  const { openEditFormDrawer } = useDrawers();
  const { t } = useTranslation();
  const { formatTimeNormalized, isAMPM } = useFormats();
  const { profileAuthorizationInCheck } = useProfileAuthorizationCheck();
  const { hasPermissions: hasCreateOtherSitePermission } = useHasPermissions({
    entity: "ride",
    action: "create_other_site",
  });
  const systemSettingsDto = useRecoilValue(systemSettingsDtoAtom);
  const { showPassengerShiftTimeInKanban, enablePassengerGenderDistinction } =
    systemSettingsDto?.metadata ?? {};

  const passengerScopeRestrictions = useMemo(
    () => getPassengerScopeRestrictions(passenger, profileAuthorizationInCheck),
    [passenger, profileAuthorizationInCheck],
  );
  const hasScopeRestrictions = hasPassengerScopeRestrictions(
    passengerScopeRestrictions,
  );
  const hasMoveRestrictions = hasMoveScopeRestrictions({
    restrictions: passengerScopeRestrictions,
    hasCreateOtherSite: hasCreateOtherSitePermission,
  });
  const isPassengerInProfileAuthScope = !hasScopeRestrictions;
  const canRemovePassengerByScope = canRemovePassengerByProfileScope(
    passenger,
    profileAuthorizationInCheck,
    { hasCreateOtherSite: hasCreateOtherSitePermission },
  );

  const iconColor = getPassengerSexColor({
    sex: passenger.sex,
    isSelected: chosen ?? false,
    isGenderDistinctionEnabled: enablePassengerGenderDistinction ?? false,
  });
  const {
    id: passengerId,
    shiftHour,
    bookingId,
    firstName,
    lastName,
  } = passenger;
  const { hasPermissions: isEditBookingPermission } = useHasPermissions({
    entity: "booking",
    action: "update",
  });
  const { hasPermissions: isUpdateRidePermission } = useHasPermissions({
    entity: "ride",
    action: "update",
  });
  const isTimeClickable = Boolean(isEditBookingPermission && bookingId);

  const isPassengerDraggable =
    canDragPassengerByProfileScope({
      passenger,
      profileAuthorizationInCheck,
      hasCreateOtherSite: hasCreateOtherSitePermission,
    }) && isUpdateRidePermission;
  const passengerNameColor = getPassengerNameColor({
    isScopeRestricted: hasScopeRestrictions,
  });
  const scopeRestrictionTooltipLines = useMemo(
    () =>
      getPassengerScopeRestrictionTooltipKeys(
        getPassengerMoveScopeRestrictions({
          restrictions: passengerScopeRestrictions,
          hasCreateOtherSite: hasCreateOtherSitePermission,
        }),
      ).map(key => t(key)),
    [passengerScopeRestrictions, hasCreateOtherSitePermission, t],
  );
  const scopeRestrictionTooltip = (
    <>
      {scopeRestrictionTooltipLines.map(line => (
        <div key={line}>{line}</div>
      ))}
    </>
  );
  const passengerDragSlot = getKanbanPassengerDragSlot({
    isPassengerDraggable,
    isDragClone,
    hasScopeRestrictions: hasMoveRestrictions,
  });

  const canRemovePassenger =
    isUpdateRidePermission && canRemovePassengerByScope;

  const passengerDragId = generateDragIdKanbanPassenger(
    rideDto.id || 0,
    passengerId,
  );

  const countPaxText =
    dragSelectedIds && dragSelectedIds?.length > 1
      ? `+${dragSelectedIds.length}pax`
      : "";

  const passengerFullName = isDragClone
    ? `${firstName || ""} ${lastName || ""}  ${countPaxText}`
    : `${firstName || ""} ${lastName || ""}`;
  const isHubCompanion = isHubRideCompanionPassenger({
    passenger,
    rideDto,
  });
  const companionLabel = isHubCompanion
    ? t("ride.HUB_RIDE_COMPANION")
    : undefined;

  const { decorateValue } = useDecorateWithMarkLocal();

  const passengerDecoratedWithMarkName = useMemo(() => {
    return decorateValue(passengerFullName);
  }, [decorateValue, passengerFullName]);

  const stationTimeOrShiftTime = useMemo(() => {
    const timeValue = showPassengerShiftTimeInKanban
      ? shiftHour
      : getRidePassengerStationTime(passengerId, rideDto);
    return timeValue ? formatTimeNormalized(timeValue) : NO_VALUE_DISPLAY;
  }, [
    formatTimeNormalized,
    passengerId,
    rideDto,
    shiftHour,
    showPassengerShiftTimeInKanban,
  ]);

  const stationName = useMemo(
    () => getRidePassengerStationName(passengerId, rideDto),
    [passengerId, rideDto],
  );

  const handlePassengerNameClick = useEvent(() => {
    if (isDragClone || !isPassengerInProfileAuthScope) {
      return;
    }

    openEditFormDrawer({
      entity: "passenger",
      id: passengerId,
    });
  });

  const handleTimeClick = useEvent(() => {
    if (isDragClone || !isEditBookingPermission) {
      return;
    }

    openEditFormDrawer({
      entity: "booking",
      id: bookingId,
    });
  });
  const stationFromOrderIndex = stationIdsOrderIndexMap.get(
    passenger.from as number,
  );
  const stationToOrderIndex = stationIdsOrderIndexMap.get(
    passenger.to as number,
  );
  const stationFromInfoWithOrderIndex: TStationInfoWithOrderIndex = {
    orderIndex: stationFromOrderIndex as number,
    ...(rideDto?.metadata?.stationsInfo?.at(
      stationFromOrderIndex as number,
    ) as IStationInfo),
  };
  const stationToInfoWithOrderIndex: TStationInfoWithOrderIndex = {
    orderIndex: stationToOrderIndex as number,
    ...(rideDto?.metadata?.stationsInfo?.at(
      stationToOrderIndex as number,
    ) as IStationInfo),
  };

  const renderContent = (): JSX.Element => {
    return (
      <KanbanGridPassengerRowStyled
        $isDragClone={isDragClone}
        className={clsx({ ampm: isAMPM })}
      >
        {isUpdateRidePermission &&
          passengerDragSlot === KanbanPassengerDragSlotEnum.Grip && (
            <KanbanPassengerDragGrip />
          )}
        {isUpdateRidePermission &&
          passengerDragSlot === KanbanPassengerDragSlotEnum.Restriction && (
            <KanbanPassengerScopeRestrictionMarker
              tooltipContent={scopeRestrictionTooltip}
            />
          )}
        {isUpdateRidePermission &&
          passengerDragSlot === KanbanPassengerDragSlotEnum.Placeholder && (
            <KanbanPassengerDragGripPlaceholder />
          )}
        <RidePassengerTooltip
          passenger={passenger}
          companionLabel={companionLabel}
          rideSiteId={rideDto?.siteId}
          rideSiteName={rideDto?.siteName}
          rtlStrippedBottomLeftPlacement
          tooltipDisabled={isDragClone}
        >
          <span className="kanban-passenger-tooltip-trigger">
            <span className="checkbox_user">
              <CheckboxUser
                width={14}
                height={14}
                checked={chosen || false}
                setChecked={() => setChosenToggle?.()}
                catchARide={passenger?.catchARide}
                color={iconColor}
              />
            </span>
            {!isTimeClickable && (
              <span data-e2e="ride-station-time" className="time">
                {stationTimeOrShiftTime}
              </span>
            )}
            {isTimeClickable && (
              <span
                data-e2e="ride-station-time"
                className="time pointer"
                onClick={handleTimeClick}
                onKeyDown={onActivateKeyDown(handleTimeClick)}
                role="button"
                tabIndex={0}
              >
                {stationTimeOrShiftTime}
              </span>
            )}
            <span className="family_name_wrap">
              {isHubCompanion && (
                <HubRideCompanionMark className="hub-ride-companion-mark" />
              )}
              <span className="passenger-name-tooltip-wrap">
                <span
                  className={clsx(
                    "family_name truncate-text passenger-name-truncate-inner",
                    {
                      pointer: isPassengerInProfileAuthScope,
                    },
                  )}
                  onClick={handlePassengerNameClick}
                  onKeyDown={onActivateKeyDown(handlePassengerNameClick)}
                  role="button"
                  tabIndex={0}
                  style={{ color: passengerNameColor }}
                >
                  {passengerDecoratedWithMarkName}
                </span>
              </span>
            </span>
          </span>
        </RidePassengerTooltip>
        <PassengerStations
          passenger={passenger}
          stationFromInfo={stationFromInfoWithOrderIndex}
          stationToInfo={stationToInfoWithOrderIndex}
          direction={rideDto.direction}
        />
        <FramerTooltip content={`${CURRENT_STATION_EMOJI} ${stationName}`}>
          <span className="station truncate-text">{stationName}</span>
        </FramerTooltip>

        <RideBoardingIndicator passenger={passenger} />
        {canRemovePassenger && (
          <span className={clsx({ chosen, bin: true })}>
            <FramerTooltip content={t("ride.RemovePassenger")}>
              <SVG_MUI.Trash2SVG
                className="pointer"
                onClick={e => deletePassengerHandler?.(e, passengerId)}
              />
            </FramerTooltip>
          </span>
        )}
      </KanbanGridPassengerRowStyled>
    );
  };

  return (
    <>
      {!isDragClone && isPassengerDraggable && (
        <DraggableWrapper
          id={passengerDragId}
          dragData={{ type: DragTypeEnum.KANBAN_PASSENGER, payload: props }}
          isListeners={false}
        >
          {renderContent()}
        </DraggableWrapper>
      )}
      {(isDragClone || !isPassengerDraggable) && <>{renderContent()}</>}
    </>
  );
};
